import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Modal } from 'reactstrap';
import { del, get, post, put } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { ManageRXTemplatetColumns } from './tableColumns';
import { RX_TEMPLATES_URL, PATIENT_URL } from 'helpers/url_helper';
import { showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import { AvForm, AvField } from 'availity-reactstrap-validation';
import Select from "react-select";
import Addrows from 'pages/utils/incrementalRows';
import { useTranslation } from 'react-i18next';

const ManageRxTemplate = () => {
    const { t } = useTranslation();
    const [isForm, setIsForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [rows, setRows] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });
    const [doctors, setDoctors] = useState([]);
    const [viewingTemplate, setViewingTemplate] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        const { success, body } = await get(`${RX_TEMPLATES_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}`);
        if (success) {
            setRows(body);
            setLoading(false);
        }
    };

    useEffect(fetchData, [pagination]);

    const getExtraData = async () => {
        const { success, body } = await get(`${PATIENT_URL}/options?_type=doctors`);
        if (success) {
            setDoctors(body);
        }
    };

    useEffect(() => {
        getExtraData();
    }, []);

    const handleToggle = () => {
        setIsForm(!isForm);
        setEditMode(false);
        setEditingId(null);
        setFormData({});
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const url = editMode ? `${RX_TEMPLATES_URL}/${editingId}` : RX_TEMPLATES_URL;
        const method = editMode ? put : post;

        const { success } = await method(url, formData);

        if (success) {
            showSuccessAlert(editMode ? t('RX Template updated successfully!') : t('RX Template created successfully!'));
            setIsForm(false);
            setFormData({});
            setEditMode(false);
            setEditingId(null);
            fetchData();
        }
    };

    const handleInvalidSubmit = () => {
        alert(t("Please enter all required fields correctly."));
    };

    const handleMultiRowData = (data) => {
        if (data?.length > 0) {
            setFormData((prev) => ({
                ...prev,
                medicins: JSON.stringify(data),
            }));
        }
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditMode(true);
        setEditingId(row.id);
        setIsForm(true);
        setLoading(false);
    };

    const handleDelete = async (id, sts) => {
        const action = sts === 1 ? t('De-activate') : t('Activate');

        const { isConfirmed } = await showConfirmAlert(
            `${action} ${t('this RX Template')}`,
            `${t('Do you really want to')} ${action} ${t('this RX Template')}?`,
            `${t('Yes')}, ${action} ${t('it')}!`,
            `${action}d!`,
            `${t('RX Template has been updated successfully.')}`
        );

        if (isConfirmed) {
            const { success } = await del(`${RX_TEMPLATES_URL}/${id}?sts=${sts}`);
            if (success) {
                fetchData();
            }
        }
    };

    const handleViewMedicines = (row, medsList) => {
        let parsed = medsList || [];
        if (!parsed || parsed.length === 0) {
            const raw = row.medicins || row.medicine || row.medicines;
            try {
                parsed = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
            } catch (e) {
                parsed = [];
            }
        }
        setViewingTemplate({
            title: row.title,
            doctor: row.doctor?.name || (row.doctor_code && row.doctor_code !== 'ALL' ? row.doctor_code : t('All Clinic Doctors')),
            note: row.note,
            medicines: parsed
        });
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Datatables
                        isSearch={true}
                        columns={ManageRXTemplatetColumns({ "edit": handleEdit, "delete": handleDelete, "viewMedicines": handleViewMedicines }, pagination)}
                        showTableOnly={true}
                        rowsLength={rows?.totalItems || 0}
                        rows={rows.items || []}
                        keyField={'id'}
                        handleAddButton={handleToggle}
                        title={t("All Rx Templates")}
                        isAdd={true}
                        isTableHead={true}
                        loading={loading}
                        ssr={setPagination}
                    />
                </CardBody>
            </Card>

            {/* Modal for viewing all medicines of a template */}
            <Modal isOpen={Boolean(viewingTemplate)} toggle={() => setViewingTemplate(null)} size="lg" centered>
                <div className="modal-header border-bottom bg-light py-3 px-4">
                    <div className="d-flex align-items-center gap-2.5">
                        <div className="rounded-3 p-2 bg-primary text-white font-size-18 d-flex align-items-center justify-content-center">
                            <i className="fas fa-prescription-bottle-alt" />
                        </div>
                        <div>
                            <h5 className="modal-title font-weight-bold text-slate-900 mb-0 font-size-16">
                                {viewingTemplate?.title || t("Prescription Protocol")}
                            </h5>
                            <span className="text-muted font-size-12">
                                {t("Prescribed For")}: <strong>{viewingTemplate?.doctor}</strong>
                            </span>
                        </div>
                    </div>
                    <button type="button" className="btn-close" onClick={() => setViewingTemplate(null)} aria-label="Close" />
                </div>

                <div className="modal-body p-4">
                    {/* Clinical Instructions / Notes */}
                    {viewingTemplate?.note && (
                        <div className="p-3 mb-3.5 rounded-3 bg-light border d-flex align-items-start gap-2">
                            <i className="fas fa-info-circle text-primary font-size-15 mt-0.5" />
                            <div>
                                <span className="font-weight-bold text-slate-800 font-size-12 d-block mb-0.5">{t("Clinical Instructions / Patient Advice")}:</span>
                                <span className="text-slate-600 font-size-12">{viewingTemplate.note}</span>
                            </div>
                        </div>
                    )}

                    {/* Medications Table */}
                    <div className="table-responsive rounded-3 border overflow-hidden">
                        <table className="table table-hover mb-0 align-middle font-size-13">
                            <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                                <tr>
                                    <th style={{ width: '45px', textAlign: 'center' }} className="text-muted font-size-11 font-weight-bold">#</th>
                                    <th className="text-slate-700 font-size-11 font-weight-bold">{t("MEDICINE NAME")}</th>
                                    <th className="text-slate-700 font-size-11 font-weight-bold">{t("DOSAGE")}</th>
                                    <th className="text-slate-700 font-size-11 font-weight-bold">{t("FREQUENCY")}</th>
                                    <th className="text-slate-700 font-size-11 font-weight-bold">{t("DURATION")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(viewingTemplate?.medicines || []).map((med, mIdx) => {
                                    const medName = typeof med === 'string' ? med : (typeof med?.medicine === 'object' ? (med.medicine?.label || med.medicine?.name) : (med?.medicine || med?.medicine_name || med?.name || t("Medicine")));
                                    const doseVal = typeof med?.dose === 'object' ? (med.dose?.label || med.dose?.value) : (med?.dose || med?.dosage || '1 Tab');
                                    const freqVal = typeof med?.frequent === 'object' ? (med.frequent?.label || med.frequent?.value) : (med?.frequent || med?.frequency || '1-0-1');
                                    const durVal = typeof med?.duration === 'object' ? (med.duration?.label || med.duration?.value) : (med?.duration || '5 days');
                                    return (
                                        <tr key={`med-item-${mIdx}`}>
                                            <td className="text-center text-muted font-weight-bold font-size-12">{mIdx + 1}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <i className="fas fa-pills text-primary font-size-12 opacity-75" />
                                                    <strong className="text-slate-900 font-size-13">{medName}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-slate-800 border font-size-11 px-2 py-1">
                                                    {doseVal}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle font-size-11 font-weight-semibold px-2 py-1">
                                                    {freqVal}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-slate-800 border font-size-11 px-2 py-1">
                                                    {durVal}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="modal-footer bg-light border-top py-2 px-4 d-flex justify-content-end">
                    <button type="button" className="btn btn-secondary font-weight-semibold px-3 py-1.5 font-size-13" onClick={() => setViewingTemplate(null)}>
                        {t("Close")}
                    </button>
                </div>
            </Modal>

            {/* Modal for Creating / Editing Template */}
            <Modal isOpen={isForm} toggle={handleToggle} size="xl" centered>
                <AvForm onValidSubmit={handleSubmit} onInvalidSubmit={handleInvalidSubmit} model={formData}>
                    <div className="modal-header">
                        <h5 className="modal-title font-weight-bold">
                            {editMode ? t('Update Prescription Template') : t('Create Prescription Template')}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4">
                        <Row className="g-3">
                            <Col md={8}>
                                <div className="mb-2">
                                    <AvField
                                        name="title"
                                        label={t("Rx Template Title *")}
                                        type="text"
                                        placeholder={t("e.g. Standard Post-Extraction Prescription")}
                                        value={formData.title || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Rx Template title")}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Prescribing Doctor")}</label>
                                    <Select
                                        id="doctor"
                                        className="basic-single"
                                        isClearable={true}
                                        isSearchable={true}
                                        options={doctors}
                                        isDisabled={editMode}
                                        placeholder={t("All Clinic Doctors (Global)")}
                                        value={doctors.find(d => d.value === formData.doctor_code) || null}
                                        onChange={(sel) => setFormData(prev => ({ ...prev, doctor_code: sel?.value || '' }))}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div className="my-3 p-3 bg-light rounded border">
                            <CardTitle tag="h5" className="mb-3 font-size-14 text-slate-800 font-weight-bold">
                                {t("Prescribed Medications & Dosages")}
                            </CardTitle>
                            <Addrows
                                callback={handleMultiRowData}
                                tData={formData.medicins ? (typeof formData.medicins === 'string' ? JSON.parse(formData.medicins) : formData.medicins) : null}
                            />
                        </div>

                        <Row>
                            <Col md={12}>
                                <div className="mb-2">
                                    <AvField
                                        name="note"
                                        label={t("Clinical Notes / Patient Instructions")}
                                        type="textarea"
                                        rows={3}
                                        placeholder={t("e.g. Take medications after meals. Avoid hot fluids for 24 hours.")}
                                        value={formData.note || ''}
                                        onChange={handleChange}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-danger" onClick={handleToggle}>
                            {t("Cancel")}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? t('Processing...') : (editMode ? t('Update Template') : t('Save Template'))}
                        </button>
                    </div>
                </AvForm>
            </Modal>
        </React.Fragment>
    );
};

export default ManageRxTemplate;
